import { useLocalStorageManager } from '@0x234/common-vue/composables/local-storage-manager';
import { RequestManager } from '@jaseeey/request-manager';
import axios from 'axios';
import { defineStore } from 'pinia';
import env from 'src/configs/env';
import { computed, ref } from 'vue';

export const useLocationStore = defineStore('location', () => {
    const localStorageManager = useLocalStorageManager();

    const browserLocation = ref(null);
    const isBrowserLocationLoading = ref(false);

    const browserPosition = localStorageManager.storeRef('location/baseCoords', null);
    const isBrowserPositionLoading = ref(false);
    const isBrowserPositionLoaded = computed(() => !!browserPosition.value);
    const isBrowserPositionBlocked = ref(false);

    const countries = ref([]);
    const isCountriesLoading = ref(false);
    const isCountriesLoaded = ref(false);

    const divisions = ref([]);
    const isDivisionsLoading = ref(false);
    const isDivisionsLoaded = ref(false);

    const locations = ref([]);

    let loadBrowserPositionPromise = null;

    let getLocationsCancelToken = null;

    async function loadBrowserLocation() {
        if (browserLocation.value) {
            return browserLocation.value;
        }
        try {
            isBrowserLocationLoading.value = true;
            await loadBrowserPosition.bind(this)();
            const locations = await getLocations.bind(this)();
            if (!locations.length) {
                throw new Error('Could not find any locations.');
            }
            browserLocation.value = locations[0];
            return browserLocation.value;
        } finally {
            isBrowserLocationLoading.value = false;
        }
    }

    function loadBrowserPosition() {
        if (!loadBrowserPositionPromise) {
            loadBrowserPositionPromise = new Promise((resolve, reject) => {
                if (navigator.geolocation) {
                    isBrowserPositionLoading.value = true;
                    navigator.geolocation.getCurrentPosition(
                        position => {
                            browserPosition.value = {
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude
                            };
                            resolve(browserPosition.value);
                            isBrowserPositionLoading.value = false;
                            isBrowserPositionBlocked.value = false;
                        },
                        err => {
                            if (err.constructor.name === 'GeolocationPositionError' && err.code === 1) {
                                isBrowserPositionBlocked.value = true;
                            } else {
                                console.error('Error getting browser location:', err);
                            }
                            reject(err);
                            isBrowserPositionLoading.value = false;
                        }
                    );
                } else {
                    const err = new Error('Geolocation is not supported by this browser.');
                    console.error(err);
                    isBrowserPositionLoading.value = false;
                    reject(err);
                }
            }).finally(() => {
                loadBrowserPositionPromise = null;
            });
        }
        return loadBrowserPositionPromise;
    }

    async function loadAllCountries() {
        // As countries will almost never change, there is no need to expire the loaded records.
        if (isCountriesLoaded.value) {
            return countries;
        }
        isCountriesLoading.value = true;
        return await RequestManager.call(this.$api, 'get', '/public/locations/countries', {}, {
            headers: { authorization: generateBasicAuthToken() }
        }, res => {
            countries.value = res.data;
            isCountriesLoading.value = false;
            isCountriesLoaded.value = true;
            return countries.value;
        }, err => {
            isCountriesLoading.value = false;
            isCountriesLoaded.value = false;
            throw err;
        });
    }

    async function loadAllCountryDivisions(isoCode) {
        // Similar to countries, if divisions are already loaded, just return them.
        if (isDivisionsLoaded.value) {
            return divisions.value;
        }
        isDivisionsLoading.value = true;
        return await RequestManager.call(this.$api, 'get', `/public/locations/countries/${isoCode}/divisions`, {}, {
            headers: { authorization: generateBasicAuthToken() }
        }, res => {
            divisions.value = res.data.sort((a, b) => a.name.localeCompare(b.name));
            isDivisionsLoading.value = false;
            isDivisionsLoaded.value = true;
            return divisions.value;
        }, err => {
            isDivisionsLoading.value = false;
            isDivisionsLoaded.value = false;
            throw err;
        });
    }

    async function getLocations(query, refLocationId = undefined, allowIntl = false) {
        if (getLocationsCancelToken) {
            getLocationsCancelToken.cancel('Request superseded');
        }
        getLocationsCancelToken = axios.CancelToken.source();
        try {
            const params = {
                lat: browserPosition.value?.latitude ?? undefined,
                lon: browserPosition.value?.longitude ?? undefined,
                ref: refLocationId ?? undefined
            };
            if (query) {
                params.q = query.toLowerCase();
            }
            if (!allowIntl) {
                params.scope = 2077456; // Location ID for Australia
            }
            const response = await this.$api.get('/public/locations', {
                params,
                headers: { authorization: generateBasicAuthToken() },
                cancelToken: getLocationsCancelToken.token
            });
            locations.value = response.data;
            return locations.value;
        } catch (err) {
            if (!axios.isCancel(err)) {
                throw err;
            }
        } finally {
            getLocationsCancelToken = null;
        }
    }

    async function loadLocation(locationId) {
        const cachedLocation = locations.value.find(loc => loc.id === locationId);
        if (cachedLocation) {
            return cachedLocation;
        }
        const location = (await this.$api.get(`/public/locations/${locationId}`, {
            headers: { authorization: generateBasicAuthToken() }
        })).data;
        const cachedLocationIndex = locations.value.findIndex(loc => loc.id === location.id);
        if (cachedLocationIndex !== -1) {
            locations.value[cachedLocationIndex] = location;
        } else {
            locations.value.push(location);
        }
        return location;
    }

    async function getCountry(isoCode) {
        return (await RequestManager.call(this.$api, 'get', `/public/locations/countries/${isoCode}`, {}, {
            headers: { authorization: generateBasicAuthToken() }
        })).data;
    }

    async function getCountryDivisions(isoCode) {
        return (await this.$api.get(`/public/locations/countries/${isoCode}/divisions`, {
            headers: { authorization: generateBasicAuthToken() }
        })).data;
    }

    function generateBasicAuthToken() {
        return `Basic ${btoa([env.siteUsername, env.sitePassword].join(':'))}`;
    }

    return {
        browserLocation,
        isBrowserLocationLoading,
        browserPosition,
        isBrowserPositionLoaded,
        isBrowserPositionLoading,
        isBrowserPositionBlocked,
        countries,
        isCountriesLoading,
        isCountriesLoaded,
        divisions,
        isDivisionsLoading,
        isDivisionsLoaded,
        locations,
        loadBrowserLocation,
        loadBrowserPosition,
        loadAllCountries,
        loadAllCountryDivisions,
        getLocations,
        loadLocation,
        getCountry,
        getCountryDivisions
    };
}, {
    preventHydrate: ['browserLocation']
});
